import { useCallback, useState } from 'react';

export interface UseAccessibleInteractionOptions {
    onClick?: (event: React.MouseEvent | React.KeyboardEvent) => void;
    onFocus?: (event: React.FocusEvent) => void;
    onBlur?: (event: React.FocusEvent) => void;
    onHover?: (isHovering: boolean) => void;
    disabled?: boolean;
    role?: string;
    tabIndex?: number;
    ariaLabel?: string;
    ariaDescribedBy?: string;
    ariaExpanded?: boolean;
    ariaSelected?: boolean;
    ariaPressed?: boolean;
    enableKeyboardActivation?: boolean;
    activationKeys?: string[];
    preventDefaultOnActivation?: boolean;
    enableTouchEvents?: boolean;
    preventClickAfterTouch?: boolean;
}

export interface UseAccessibleInteractionResult {
    interactionProps: {
        onClick?: (event: React.MouseEvent) => void;
        onKeyDown?: (event: React.KeyboardEvent) => void;
        onFocus?: (event: React.FocusEvent) => void;
        onBlur?: (event: React.FocusEvent) => void;
        onMouseEnter?: (event: React.MouseEvent) => void;
        onMouseLeave?: (event: React.MouseEvent) => void;
        onTouchEnd?: (event: React.TouchEvent) => void;
        role?: string;
        tabIndex?: number;
        'aria-label'?: string;
        'aria-describedby'?: string;
        'aria-expanded'?: boolean;
        'aria-selected'?: boolean;
        'aria-pressed'?: boolean;
        'aria-disabled'?: boolean;
    };
}

const DEFAULT_ACTIVATION_KEYS = ['Enter', ' ']; // Enter and Space
// Browsers synthesize a click this soon after touchend.
const SYNTHETIC_CLICK_WINDOW_MS = 300;

export const useAccessibleInteraction = (
    options: UseAccessibleInteractionOptions = {}
): UseAccessibleInteractionResult => {
    const {
        onClick,
        onFocus,
        onBlur,
        onHover,
        disabled = false,
        role = 'button',
        tabIndex = 0,
        ariaLabel,
        ariaDescribedBy,
        ariaExpanded,
        ariaSelected,
        ariaPressed,
        enableKeyboardActivation = true,
        activationKeys = DEFAULT_ACTIVATION_KEYS,
        preventDefaultOnActivation = true,
        enableTouchEvents = false,
        preventClickAfterTouch = false
    } = options;

    const [lastTouchEnd, setLastTouchEnd] = useState(0);

    const handleClick = useCallback((event: React.MouseEvent) => {
        if (disabled) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }

        // Prevent click if it's a synthetic click after touch
        if (preventClickAfterTouch && Date.now() - lastTouchEnd < SYNTHETIC_CLICK_WINDOW_MS) {
            return;
        }

        if (onClick) {
            onClick(event);
        }
    }, [disabled, onClick, lastTouchEnd, preventClickAfterTouch]);

    const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
        if (disabled) {
            return;
        }

        const isActivationKey = activationKeys.includes(event.key);
        
        if (enableKeyboardActivation && isActivationKey) {
            if (preventDefaultOnActivation) {
                event.preventDefault();
            }
            
            if (onClick) {
                onClick(event);
            }
        }
    }, [disabled, enableKeyboardActivation, activationKeys, preventDefaultOnActivation, onClick]);

    const handleTouchEnd = useCallback((event: React.TouchEvent) => {
        if (disabled) {
            event.preventDefault();
            return;
        }

        setLastTouchEnd(Date.now());
        
        if (onClick) {
            // Prevent the synthetic click event that follows touchend
            event.preventDefault();
            onClick(event as any);
        }
    }, [disabled, onClick]);

    const handleFocus = useCallback((event: React.FocusEvent) => {
        if (disabled) {
            return;
        }

        if (onFocus) {
            onFocus(event);
        }
    }, [disabled, onFocus]);

    const handleBlur = useCallback((event: React.FocusEvent) => {
        if (onBlur) {
            onBlur(event);
        }
    }, [onBlur]);

    const handleMouseEnter = useCallback((_event: React.MouseEvent) => {
        if (disabled) {
            return;
        }

        if (onHover) {
            onHover(true);
        }
    }, [disabled, onHover]);

    const handleMouseLeave = useCallback((_event: React.MouseEvent) => {
        if (onHover) {
            onHover(false);
        }
    }, [onHover]);

    const interactionProps: UseAccessibleInteractionResult['interactionProps'] = {
        onClick: handleClick,
        onKeyDown: enableKeyboardActivation ? handleKeyDown : undefined,
        onFocus: handleFocus,
        onBlur: handleBlur,
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        role: !disabled ? role : undefined,
        tabIndex: disabled ? -1 : tabIndex,
    };

    // Optional props are omitted, not set to undefined, when their option is absent.
    if (enableTouchEvents) {
        interactionProps.onTouchEnd = handleTouchEnd;
    }
    if (ariaLabel) {
        interactionProps['aria-label'] = ariaLabel;
    }
    if (ariaDescribedBy) {
        interactionProps['aria-describedby'] = ariaDescribedBy;
    }
    if (ariaExpanded !== undefined) {
        interactionProps['aria-expanded'] = ariaExpanded;
    }
    if (ariaSelected !== undefined) {
        interactionProps['aria-selected'] = ariaSelected;
    }
    if (ariaPressed !== undefined) {
        interactionProps['aria-pressed'] = ariaPressed;
    }
    if (disabled) {
        interactionProps['aria-disabled'] = true;
    }

    return { interactionProps };
};

export const useButtonInteraction = (
    onClick: (event: React.MouseEvent | React.KeyboardEvent) => void,
    options: Omit<UseAccessibleInteractionOptions, 'onClick' | 'role'> = {}
) => {
    return useAccessibleInteraction({
        ...options,
        onClick,
        role: 'button'
    });
};
